import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts';

export interface VulnEvolutionPoint {
  scan_id: number;
  scan_name: string;
  date: string;
  timestamp: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  info: number;
  total: number;
}

type ChartViewMode = 'total' | 'severity';

interface VulnerabilityEvolutionChartProps {
  data: VulnEvolutionPoint[];
  height?: number;
  showTotal?: boolean;
  showInfo?: boolean;
  title?: string;
}

const SEVERITY_COLORS = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#d97706',
  low: '#16a34a',
  info: '#3b82f6',
  total: '#6366f1',
};

const VulnerabilityEvolutionChart: React.FC<VulnerabilityEvolutionChartProps> = ({
  data,
  height = 350,
  showTotal = true,
  showInfo = false,
  title,
}) => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ChartViewMode>('severity');
  // Individual severity toggles
  const [severityToggles, setSeverityToggles] = useState({
    critical: true,
    high: true,
    medium: true,
    low: true,
    info: showInfo,
    total: showTotal,
  });

  // Toggle a specific severity
  const toggleSeverity = (severity: keyof typeof severityToggles) => {
    setSeverityToggles(prev => ({
      ...prev,
      [severity]: !prev[severity],
    }));
  };

  if (!data || data.length === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: height,
        backgroundColor: 'var(--color-background-secondary)',
        borderRadius: '8px',
        border: '1px dashed var(--color-border-secondary)',
        color: 'var(--color-text-tertiary)',
      }}>
        <div style={{ textAlign: 'center' }}>
          <i className="fas fa-chart-line" style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.5 }}></i>
          <p style={{ margin: 0 }}>{t('charts.noScanDataAvailable')}</p>
          <p style={{ margin: 0, fontSize: '0.85rem' }}>{t('charts.completeAtLeast2Scans')}</p>
        </div>
      </div>
    );
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = data.find(d => d.date === label);

      // Deduplicate payload entries (Area and Line with same dataKey both appear)
      const seen = new Set<string>();
      const uniquePayload = payload.filter((entry: any) => {
        const key = entry.dataKey;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      return (
        <div style={{
          backgroundColor: 'var(--color-background)',
          border: '1px solid var(--color-border-primary)',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: 'var(--color-card-shadow)',
          minWidth: '180px',
        }}>
          <p style={{
            margin: '0 0 8px 0',
            fontWeight: 600,
            fontSize: '0.9rem',
            color: 'var(--color-text-primary)',
            borderBottom: '1px solid var(--color-border-primary)',
            paddingBottom: '6px',
          }}>
            {dataPoint?.scan_name || label}
          </p>
          <p style={{ margin: '0 0 6px 0', fontSize: '0.8rem', color: 'var(--color-text-tertiary)' }}>
            {dataPoint?.timestamp
              ? new Date(dataPoint.timestamp).toLocaleString(undefined, {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              : dataPoint?.date}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {uniquePayload.map((entry: any, index: number) => {
              // Get translated name for severity
              const severityKey = entry.dataKey as string;
              const displayName = severityKey === 'total'
                ? t('charts.total')
                : t(`severity.${severityKey}`);

              return (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.85rem',
                  }}>
                    <span style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '2px',
                      backgroundColor: entry.color,
                    }}></span>
                    <span>{String(displayName)}:</span>
                  </div>
                  <span style={{
                    fontWeight: 600,
                    color: entry.color,
                    fontSize: '0.85rem',
                  }}>
                    {entry.value}
                  </span>
                </div>
              );
            })}
          </div>
          {viewMode === 'total' && dataPoint && (
            <div style={{
              marginTop: '8px',
              paddingTop: '6px',
              borderTop: '1px solid var(--color-border-primary)',
              display: 'flex',
              justifyContent: 'space-between',
            }}>
              <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{t('charts.total')}:</span>
              <span style={{ fontWeight: 700, color: SEVERITY_COLORS.total }}>
                {dataPoint.total}
              </span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {title && (
            <h3 style={{
              margin: 0,
              fontSize: '1.1rem',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <i className="fas fa-chart-line" style={{ color: 'var(--color-purple-text)' }}></i>
              {title}
            </h3>
          )}
          {/* View Mode Toggle */}
          <div style={{
            display: 'flex',
            gap: '4px',
            background: 'var(--color-surface-tertiary)',
            borderRadius: '8px',
            padding: '4px',
          }}>
            <button
              onClick={() => setViewMode('severity')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 500,
                cursor: 'pointer',
                background: viewMode === 'severity' ? 'var(--color-purple-text)' : 'transparent',
                color: viewMode === 'severity' ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
                transition: 'all 0.2s ease',
              }}
            >
              {t('charts.bySeverity')}
            </button>
            <button
              onClick={() => setViewMode('total')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: 500,
                cursor: 'pointer',
                background: viewMode === 'total' ? 'var(--color-purple-text)' : 'transparent',
                color: viewMode === 'total' ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
                transition: 'all 0.2s ease',
              }}
            >
              {t('charts.totalOnly')}
            </button>
          </div>
        </div>

        {/* Severity Toggle Buttons - only show in severity view */}
        {viewMode === 'severity' && (
          <div style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginRight: '4px' }}>
              {t('charts.show')}:
            </span>
            {(['critical', 'high', 'medium', 'low', 'info'] as const).map((severity) => {
              // Hide info toggle if showInfo prop is false
              if (severity === 'info' && !showInfo) return null;

              const isActive = severityToggles[severity];
              const color = SEVERITY_COLORS[severity];

              return (
                <button
                  key={severity}
                  onClick={() => toggleSeverity(severity)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    border: `2px solid ${isActive ? color : 'var(--color-border-primary)'}`,
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    background: isActive ? `${color}15` : 'var(--color-background-secondary)',
                    color: isActive ? color : 'var(--color-text-muted)',
                    transition: 'all 0.2s ease',
                    opacity: isActive ? 1 : 0.6,
                  }}
                >
                  <span style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '3px',
                    backgroundColor: isActive ? color : 'var(--color-border-secondary)',
                    transition: 'background-color 0.2s ease',
                  }}></span>
                  {t(`severity.${severity}`)}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 5, right: 30, left: 0, bottom: 40 }}>
          <defs>
            <linearGradient id="criticalGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={SEVERITY_COLORS.critical} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={SEVERITY_COLORS.critical} stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="highGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={SEVERITY_COLORS.high} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={SEVERITY_COLORS.high} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: '#6b7280' }}
            tickLine={{ stroke: '#e5e7eb' }}
            axisLine={{ stroke: '#e5e7eb' }}
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6b7280' }}
            tickLine={{ stroke: '#e5e7eb' }}
            axisLine={{ stroke: '#e5e7eb' }}
            label={{
              value: t('charts.vulnerabilities'),
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 12, fill: '#6b7280' }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          {/* Legend is hidden - we use toggle buttons above instead to avoid duplicate entries */}

          {/* Severity view - show individual severity lines based on toggles */}
          {viewMode === 'severity' && (
            <>
              {/* Critical vulnerabilities with area fill */}
              {severityToggles.critical && (
                <>
                  <Area
                    type="monotone"
                    dataKey="critical"
                    stroke={SEVERITY_COLORS.critical}
                    fill="url(#criticalGradient)"
                    strokeWidth={0}
                    legendType="none"
                  />
                  <Line
                    type="monotone"
                    dataKey="critical"
                    name={t('severity.critical')}
                    stroke={SEVERITY_COLORS.critical}
                    strokeWidth={2.5}
                    dot={{ fill: SEVERITY_COLORS.critical, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                  />
                </>
              )}

              {/* High vulnerabilities with area fill */}
              {severityToggles.high && (
                <>
                  <Area
                    type="monotone"
                    dataKey="high"
                    stroke={SEVERITY_COLORS.high}
                    fill="url(#highGradient)"
                    strokeWidth={0}
                    legendType="none"
                  />
                  <Line
                    type="monotone"
                    dataKey="high"
                    name={t('severity.high')}
                    stroke={SEVERITY_COLORS.high}
                    strokeWidth={2.5}
                    dot={{ fill: SEVERITY_COLORS.high, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, strokeWidth: 2 }}
                  />
                </>
              )}

              {/* Medium vulnerabilities */}
              {severityToggles.medium && (
                <Line
                  type="monotone"
                  dataKey="medium"
                  name={t('severity.medium')}
                  stroke={SEVERITY_COLORS.medium}
                  strokeWidth={2}
                  dot={{ fill: SEVERITY_COLORS.medium, strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5, strokeWidth: 2 }}
                />
              )}

              {/* Low vulnerabilities */}
              {severityToggles.low && (
                <Line
                  type="monotone"
                  dataKey="low"
                  name={t('severity.low')}
                  stroke={SEVERITY_COLORS.low}
                  strokeWidth={2}
                  dot={{ fill: SEVERITY_COLORS.low, strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5, strokeWidth: 2 }}
                />
              )}

              {/* Info (optional) */}
              {showInfo && severityToggles.info && (
                <Line
                  type="monotone"
                  dataKey="info"
                  name={t('severity.info')}
                  stroke={SEVERITY_COLORS.info}
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  dot={{ fill: SEVERITY_COLORS.info, strokeWidth: 1, r: 2 }}
                  activeDot={{ r: 4, strokeWidth: 1 }}
                />
              )}
            </>
          )}

          {/* Total view - show only total line with gradient fill */}
          {viewMode === 'total' && (
            <>
              <defs>
                <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={SEVERITY_COLORS.total} stopOpacity={0.4}/>
                  <stop offset="95%" stopColor={SEVERITY_COLORS.total} stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="total"
                stroke={SEVERITY_COLORS.total}
                fill="url(#totalGradient)"
                strokeWidth={0}
                legendType="none"
              />
              <Line
                type="monotone"
                dataKey="total"
                name={t('charts.totalFindings')}
                stroke={SEVERITY_COLORS.total}
                strokeWidth={3}
                dot={{ fill: SEVERITY_COLORS.total, strokeWidth: 2, r: 5 }}
                activeDot={{ r: 7, strokeWidth: 2 }}
              />
            </>
          )}

          {/* Total line only in total view - removed from severity view to prevent scale compression */}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default VulnerabilityEvolutionChart;
